import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TadManagerConfigViewStore } from "../../store";
import { TadManagerConfigView } from "../view/TadManagerConfigView";
import { TadManagerConfigUpdateStore } from "../../store";
import { TadManagerConfigUpdate } from "../update/TadManagerConfigUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TadManagerConfigModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TadManagerConfigModalContent: React.FC<TadManagerConfigModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TadManagerConfigUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TadManagerConfigViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TadManagerConfigView
            tadManagerConfigViewStore={
              storeInfo.store as TadManagerConfigViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <TadManagerConfigUpdate
            tadManagerConfigUpdateStore={
              storeInfo.store as TadManagerConfigUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
